'use client';

import { useQuery } from '@tanstack/react-query';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { Icons } from '../../../overrides/components/homepage/icons';
import { Stores } from '../../../overrides/components/homepage/stores';
import { ProductIcons } from '../../../overrides/components/product/detail/product-icons';
import { Button } from '../../../overrides/components/ui/button';
import { useNearViewport } from '@/hooks/use-near-viewport';
import { fetchPublicHomepageSection } from '@/lib/fetch-homepage-section';
import { HOMEPAGE_SECTION_STALE_MS, homepageSectionKey } from '@/lib/homepage-sections';

const spacing = 'pb-12 pt-6 sm:pb-16 sm:pt-8 md:pb-20 md:pt-10 lg:pb-24 lg:pt-12';

/** Only decorative content: the main product, category seed and schemas remain SSR. */
export function DeferredCatalogueSection({
  section,
  locale,
}: {
  section: 'product-icons' | 'category-icons' | 'stores';
  locale: string;
}) {
  const { ref, enabled } = useNearViewport();
  const { t } = useTranslations();
  const publicSection = section === 'stores' ? 'stores' : 'icons';
  const query = useQuery({
    // Match homepage: navigation between routes reuses the same query/cache entry.
    queryKey: homepageSectionKey(publicSection, locale),
    enabled,
    staleTime: HOMEPAGE_SECTION_STALE_MS,
    gcTime: 30 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
    queryFn: ({ signal }) => fetchPublicHomepageSection(publicSection, locale, signal),
  });

  let content;
  const result = query.data;
  if (result?.section === 'stores') {
    content = result.data.length ? <Stores stores={result.data} className={spacing} /> : null;
  } else if (result?.section === 'icons') {
    const icons =
      section === 'product-icons'
        ? result.data.filter(icon => icon.type?.includes('product'))
        : result.data;
    content = icons.length ? (
      section === 'product-icons' ? (
        <ProductIcons icons={icons} />
      ) : (
        <Icons icons={icons} className={spacing} />
      )
    ) : null;
  }

  return (
    <div ref={ref} data-catalogue-section={section} aria-busy={!result && !query.isError}>
      {result ? (
        content
      ) : (
        <div
          className={
            section === 'product-icons'
              ? 'min-h-40 w-full'
              : `custom-section ${spacing} ${section === 'stores' ? '' : 'min-h-40'}`
          }
        >
          {query.isError ? (
            <div role="alert">
              <Button variant="outline" onClick={() => void query.refetch()}>
                {t('text595')}
              </Button>
            </div>
          ) : section === 'stores' ? (
            <div
              aria-hidden="true"
              className="custom-container flex flex-col gap-4 md:flex-row lg:gap-6"
            >
              <div className="h-[400px] w-full max-w-[925px] rounded-[12px] bg-muted sm:h-[430px] lg:h-[500px]" />
              <div className="h-[200px] w-full rounded-[12px] bg-muted sm:h-[350px] md:h-auto md:max-w-[451px]" />
            </div>
          ) : (
            <div
              aria-hidden="true"
              className="h-40 w-full rounded-xl bg-muted motion-safe:animate-pulse"
            />
          )}
        </div>
      )}
    </div>
  );
}
